import { useState } from 'react';
import { Alert, Button, Empty, Spin } from 'antd';
import { ArrowLeftOutlined, DownloadOutlined, LeftOutlined, RightOutlined } from '@ant-design/icons';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client';
import { PersonalReportPreview } from './PersonalReportPreview';
import type { PersonalReportSummaryJson } from '../data/mockPersonalReportSummary';
import type { DirectoryItem } from './personalReportTypes';
import { message } from 'antd';

export function PersonalReportDetail({
  batchId,
  emp,
  onBack,
  onPrev,
  onNext,
  hasPrev,
  hasNext,
  onDownloadPdf,
}: {
  batchId?: number;
  emp: DirectoryItem;
  onBack: () => void;
  onPrev?: () => void;
  onNext?: () => void;
  hasPrev?: boolean;
  hasNext?: boolean;
  onDownloadPdf: (emp: DirectoryItem) => void;
}) {
  const deptLine = [emp.dept1, emp.dept2].filter(Boolean).join(' · ') || emp.dept;

  return (
    <div className="personal-report-detail">
      <header className="personal-report-detail-header">
        <div className="personal-report-detail-header-left">
          <Button
            type="text"
            icon={<ArrowLeftOutlined />}
            onClick={onBack}
            className="personal-report-detail-back"
          >
            返回人员目录
          </Button>
          <div className="personal-report-detail-identity">
            <span className="personal-report-detail-name">{emp.name}</span>
            <span className="personal-report-detail-meta">
              {deptLine}
              {emp.position ? ` · ${emp.position}` : ''}
            </span>
          </div>
        </div>
        <div className="personal-report-detail-header-right">
          {(hasPrev || hasNext) && (
            <div className="personal-report-detail-nav">
              <Button
                type="text"
                icon={<LeftOutlined />}
                disabled={!hasPrev}
                onClick={onPrev}
              />
              <Button
                type="text"
                icon={<RightOutlined />}
                disabled={!hasNext}
                onClick={onNext}
              />
            </div>
          )}
          <Button
            type="primary"
            icon={<DownloadOutlined />}
            disabled={!batchId}
            onClick={() => onDownloadPdf(emp)}
          >
            下载 PDF
          </Button>
        </div>
      </header>
      <div className="personal-report-detail-canvas">
        <div className="personal-report-detail-inner">
          <PersonalReportContent batchId={batchId} emp={emp} />
        </div>
      </div>
    </div>
  );
}

function PersonalReportContent({
  batchId,
  emp,
}: {
  batchId?: number;
  emp: DirectoryItem;
}) {
  const enabled = !!batchId;
  const qc = useQueryClient();
  const [genLoading, setGenLoading] = useState(false);
  const { data, isLoading, error } = useQuery<PersonalReportSummaryJson | null>({
    queryKey: ['personal-summary', batchId, emp.empId],
    enabled,
    queryFn: () =>
      api.get(`/reports/personal/${emp.empId}/summary`, {
        params: { batchId },
      }),
    refetchInterval: (q) => {
      const st = q.state.data?.aiQueueStatus;
      return st === 'PENDING' || st === 'RUNNING' ? 5000 : false;
    },
  });

  const handleGenerateNarrative = async () => {
    if (!batchId) return;
    setGenLoading(true);
    const hide = message.loading(`正在用 AI 生成 ${emp.name} 的报告…`, 0);
    try {
      await api.post(`/reports/personal/${emp.empId}/narrative`, null, {
        params: { batchId },
      });
      message.success('AI 报告已生成');
      await qc.invalidateQueries({
        queryKey: ['personal-summary', batchId, emp.empId],
      });
      qc.invalidateQueries({ queryKey: ['narrative-job', batchId] });
    } catch {
      /* api 拦截器已统一弹出错误提示 */
    } finally {
      hide();
      setGenLoading(false);
    }
  };

  if (!enabled) {
    return (
      <div className="personal-report-detail-state">
        <Alert type="warning" showIcon message="请先选择批次" />
      </div>
    );
  }
  if (isLoading) {
    return (
      <div className="personal-report-detail-state">
        <Spin tip="正在加载报告…" />
      </div>
    );
  }
  if (error) {
    return (
      <div className="personal-report-detail-state">
        <Alert
          type="error"
          showIcon
          message={`加载失败：${(error as Error)?.message ?? '未知错误'}`}
        />
      </div>
    );
  }
  if (!data) {
    return (
      <div className="personal-report-detail-state">
        <Empty description="该员工在当前批次没有可生成的盘点报告（缺少评价/九宫格/胜任力等数据）" />
      </div>
    );
  }

  return (
    <PersonalReportPreview
      variant="report"
      data={data}
      titleSuffix={`${emp.dept} · ${emp.position || ''}`}
      onGenerateNarrative={handleGenerateNarrative}
      generateLoading={genLoading}
    />
  );
}
